import { Analytics, Auth } from 'aws-amplify';

import Config, { TransactionId } from '../../config/appConfig';
import { sentryCaptureException } from '../../utilities/sentryCaptureException';

export enum LogLevel {
  DEBUG = 'DEBUG',
  ERROR = 'ERROR',
  INFO = 'INFO',
  WARN = 'WARN',
  VERBOSE = 'VERBOSE',
}

enum LogLevelDictionary {
  'VERBOSE' = 0,
  'DEBUG' = 1,
  'INFO' = 2,
  'WARN' = 3,
  'ERROR' = 4,
}

enum AllowedEventTypes {
  /* called when an error is thrown in the application */
  'APPLICATION_ERROR' = 'APPLICATION_ERROR',
  /* Amplify Authentication Event */
  'AUTH_EVENT' = 'AUTH_EVENT',
  /* called when app.ts renders for the first time */
  'APPLICATION_LOADED' = 'APPLICATION_LOADED',
  /* redux action was dispatched */
  'REDUX_ACTION_DISPATCHED' = 'REDUX_ACTION_DISPATCHED',
  /* route in react router changed (page change/filtering etc) */
  'ROUTE_CHANGED' = 'ROUTE_CHANGED',
  /* A common message */
  'MESSAGE' = 'MESSAGE',
}

type AnalyticsMessage = {
  Application: string;
  Message: Record<string | number | symbol, unknown>;
  MessageType?: string;
  IsError?: boolean;
  TransactionId: string;
  TimeStamp: string;
  Meta: {
    Email?: string;
    UserId?: string;
    Build?: string;
    IsOnline?: boolean;
  } | null;
};

export const constructAnalyticsMessage = (
  data: Record<string | number | symbol, unknown> | string,
): AnalyticsMessage => {
  const message: AnalyticsMessage = {
    Application: 'AIMS21',
    TransactionId,
    TimeStamp: new Date().toISOString(),
    Message: typeof data === 'string' ? { data } : data,
    Meta: null,
  };
  return message;
};

export const Events = AllowedEventTypes;
export type ValidEventTypes = keyof typeof AllowedEventTypes;

export const logEvent = async (
  eventType: ValidEventTypes,
  data: Record<string | number | symbol, unknown>,
  logLevel: LogLevel | string = 'DEBUG',
  isError = false,
) => {
  try {
    if (!Object.keys(AllowedEventTypes).includes(eventType)) {
      throw new Error(`Invalid event type [${eventType}]`);
    }

    const isSufficientLogLevel =
      LogLevelDictionary[logLevel as unknown as number] >=
      LogLevelDictionary[Config.logLevel as unknown as number];

    // eslint-disable-next-line no-console
    console.log(JSON.stringify(isSufficientLogLevel));
    if (isSufficientLogLevel) {
      // eslint-disable-next-line no-console
      console.log('record');
      // eslint-disable-next-line no-console
      console.log(Config.kinesisStreamName);
      Auth.configure({
        identityPoolId: Config.aws_cognito_identity_pool_id,
      });
      await Analytics.record(
        {
          data: {
            ...data,
            ...(isError ? { Error: true } : {}),
            Type: eventType,
          },
          streamName: Config.kinesisStreamName,
        },
        'AWSKinesis',
      );
    }
  } catch (error) {
    console.log('error:', error); // eslint-disable-line no-console
    // console logs will be removed once sentry implementation is done
    sentryCaptureException(error, '~ file: UserMenu.tsx ~ line 75 ~ getLoggedIUser ~');
  }
};

//Log an error with stack trace
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const logError = async (error: any, unhandled: boolean, info = '') => {
  const message = constructAnalyticsMessage({
    Message: error.message || error,
    Info: info,
    StackTrace: error.stack,
    IsUnhandled: unhandled,
  });

  await logEvent('APPLICATION_ERROR', message, LogLevel.ERROR, true);
};

//Call this from index.tsx to initialize global error handlers.
export const logErrors = async () => {
  window.onerror = async error => {
    await logError(error, false);
  };

  window.onunhandledrejection = async error => {
    await logError(error?.reason || error, true);
  };
};
